/*
  The DocuSign screen's view shapes, and the translation from the envelope
  endpoints into them.

  Health (`docusign/overview`, `docusign/incidents`) is live. The envelope
  endpoints are not: they answer 502/503 until the OWEHub e-sign API is deployed,
  and the provider may yet turn out to be BoldSign rather than DocuSign — the
  contract is deliberately vendor-neutral about it and will grow a `provider`
  field. So nothing here names a vendor in a label, and every envelope panel is
  built to render its "data source not connected" state as a first-class
  outcome rather than as a failure.
*/

import { ApiError } from './api/caller'
import type {
  EnvelopeGroups,
  EnvelopeRow,
  EnvelopesOverview,
  EnvelopesPage,
  SuggestedAction,
} from './api/deep-dive.types'

/* -------------------------------------------------------- envelope status */

export interface StatusBucket {
  label: string
  count: number
  /** Bar colour: in flight is blue, done is green, dead is red or grey. */
  tone: 'open' | 'done' | 'dead' | 'neutral'
}

/**
 * Statuses in lifecycle order, with the tone each one is drawn in.
 *
 * Ordered by where an envelope is in its life rather than by count, so the panel
 * keeps its shape as the numbers move — a bar chart that reorders itself every
 * poll is one nobody can read at a glance.
 */
const STATUS_ORDER: { key: string; label: string; tone: StatusBucket['tone'] }[] = [
  { key: 'created', label: 'Created', tone: 'neutral' },
  { key: 'sent', label: 'Sent', tone: 'open' },
  { key: 'delivered', label: 'Delivered', tone: 'open' },
  { key: 'signed', label: 'Signed', tone: 'done' },
  { key: 'completed', label: 'Completed', tone: 'done' },
  { key: 'declined', label: 'Declined', tone: 'dead' },
  { key: 'voided', label: 'Voided', tone: 'dead' },
  { key: 'other', label: 'Other', tone: 'neutral' },
]

/**
 * Envelopes by current status.
 *
 * **Not a funnel**, despite the `pipeline` field name and the contract's
 * funnel-shaped example. The live account returns completed 1,764 against sent
 * 634: an envelope that completed has stopped being counted as sent, and the
 * buckets sum exactly to `total`. Drawing these widest-first with each bar
 * measured against "Sent" — as this panel originally did — would state a
 * 278% completion rate.
 *
 * Reads `by_status` rather than `pipeline`. The two are identical on the wire,
 * but `by_status` is the one the payload's own notes call exact and additive,
 * and it carries `created`/`other`, without which the bars do not add up to the
 * total printed beside them.
 */
export function toStatusBuckets(overview: EnvelopesOverview | undefined): StatusBucket[] {
  const counts = overview?.by_status ?? overview?.pipeline
  if (!counts) return []

  const known = new Set(STATUS_ORDER.map((s) => s.key))
  const ordered = STATUS_ORDER.flatMap(({ key, label, tone }) =>
    // A status the account has never used is left out rather than drawn at zero.
    counts[key] ? [{ label, count: counts[key], tone }] : [],
  )

  // Anything the provider adds later still shows up, rather than silently
  // vanishing from a total it is still part of.
  const extra = Object.entries(counts)
    .filter(([key, count]) => !known.has(key) && count > 0)
    .map(([key, count]) => ({ label: key, count, tone: 'neutral' as const }))

  return [...ordered, ...extra]
}

/** Envelopes behind the panel — the sum of the buckets, per the payload's notes. */
export function statusTotal(overview: EnvelopesOverview | undefined): number | null {
  if (!overview) return null
  if (typeof overview.total === 'number') return overview.total
  const counts = overview.by_status ?? overview.pipeline
  return counts ? Object.values(counts).reduce((sum, n) => sum + n, 0) : null
}

/* ------------------------------------------------------ requests by office */

export interface RequestSource {
  /** Office or finance-partner key, e.g. "OWE [AZ, Phoenix]". */
  code: string
  count: number
  /**
   * True for the "we could not attribute these" bucket, which is not an office
   * and must not be drawn as though it were one — it is currently the largest
   * bucket by some way (1,485 of 2,533 on office), so a green top slice would
   * read as the busiest branch.
   */
  unattributed?: boolean
}

export interface RequestBreakdown {
  id: string
  label: string
  sources: RequestSource[]
}

/** Slices for the donut. Percentages are recomputed at render from these counts. */
export function toRequestSources(groups: EnvelopeGroups | undefined): RequestSource[] {
  if (!groups) return []
  const sources: RequestSource[] = groups.groups.map((g) => ({ code: g.key, count: g.count }))
  // Envelopes with no value for the grouping dimension are still envelopes —
  // dropping them would leave the donut's centre total disagreeing with the
  // period's own count.
  if (groups.unattributed > 0) {
    sources.push({ code: 'Unattributed', count: groups.unattributed, unattributed: true })
  }
  return sources
}

/* ------------------------------------------------------------- envelopes */

export interface Envelope {
  /** Unique per row — an envelope with two signers is two rows. */
  id: string
  envelopeId: string
  recipientId: string
  /** "John Doe (Acme Corp)", or just the name where no company is reported. */
  recipient: string
  sentDate: string
  /** The pill's wording, from `recipient_status` and `auth_failed`. */
  statusLabel: string
  tone: 'waiting' | 'auth_failed' | 'delivered' | 'declined' | 'neutral'
  office: string | null
  financePartner: string | null
  /** What the row's button offers; `none` means there is nothing to chase. */
  action: SuggestedAction
}

/**
 * A recipient row's status pill.
 *
 * `auth_failed` outranks the status word: a recipient who failed authentication
 * is still "sent" as far as the envelope is concerned, and showing that would
 * hide the one row on the page that is actually stuck.
 */
function toStatus(row: EnvelopeRow): { statusLabel: string; tone: Envelope['tone'] } {
  if (row.auth_failed) return { statusLabel: 'Authentication Failed', tone: 'auth_failed' }

  switch (row.recipient_status) {
    case 'sent':
    case 'delivered':
      return { statusLabel: 'Waiting for others', tone: 'waiting' }
    case 'completed':
      return { statusLabel: 'Delivered', tone: 'delivered' }
    case 'declined':
      return { statusLabel: 'Declined', tone: 'declined' }
    case 'authenticationfailed':
      return { statusLabel: 'Authentication Failed', tone: 'auth_failed' }
    default:
      // The list of recipient statuses is open-ended upstream, so an unfamiliar
      // one is shown as-is rather than swallowed into "unknown".
      return { statusLabel: row.recipient_status, tone: 'neutral' }
  }
}

export function toEnvelopes(page: EnvelopesPage | undefined): Envelope[] {
  return (page?.rows ?? []).map((row) => ({
    id: `${row.envelope_id}:${row.recipient_id}`,
    envelopeId: row.envelope_id,
    recipientId: row.recipient_id,
    recipient: row.company ? `${row.recipient} (${row.company})` : row.recipient,
    sentDate: row.sent_at.slice(0, 16).replace('T', ' ') + 'Z',
    office: row.office,
    financePartner: row.finance_partner,
    action: row.suggested_action,
    ...toStatus(row),
  }))
}

/** What the row's button says, per suggested action. `null` = nothing to chase. */
export const envelopeActionLabel: Record<SuggestedAction, string | null> = {
  send_reminder: 'Send Reminder',
  resend_link: 'Resend Link',
  none: null,
}

/* --------------------------------------------------------------- cooldown */

/**
 * The moment a rejected action becomes retryable, from a 409.
 *
 * The contract says a cooldown 409 carries `cooldown_until`, and that the button
 * should stay disabled until it passes. The field can arrive either in the
 * envelope's `data` or in a raw HTTP body depending on how the failure was
 * raised, so both are checked — and anything unparseable simply yields null,
 * which leaves the button enabled rather than stuck forever on a bad timestamp.
 */
export function cooldownUntil(error: unknown): Date | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null
  const detail = error.detail
  if (!detail || typeof detail !== 'object') return null
  const raw = (detail as { cooldown_until?: unknown }).cooldown_until
  if (typeof raw !== 'string') return null
  const date = new Date(raw)
  return Number.isNaN(date.getTime()) ? null : date
}
